import React, { useContext, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import filesize from 'filesize';
import { Box, Button } from '@mui/material';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import Toast from '../../../../components/Toast';
import { options } from '../../../../helpers/toastOptions';
import { useAuth } from '../../../../hooks/Auth';
import { useModal } from '../../../../hooks/Modal';
import { usePickup } from '../../../../hooks/Pickup';
import { ModalTitle, ModalText } from './styled';
import { ReverseCollectContext } from '../..';
import { parseCpfCnpj, parsePhone } from '../../../../helpers/parseValues';

export const ModalWishImportInvoice = () => {
  const [modalSelected, setModalSelected] = useState('wishImportInvoiceModal');
  const [isReadingFile, setIsReadingFile] = useState(false);

  const inputFileRef = useRef(null);

  const { user, userAddressList } = useAuth();
  const { Modal, closeModal } = useModal();
  const { readTaxDocument } = usePickup();

  const {
    addressList,
    setAddressList,
    setSelectedAddressIndex,
    setCollectSenderData,
    setDisabledSenderData,
    setDocumentType,
    setFileAmount,
    setInvoiceFiles,
    setHasCreatePickupWithInvoice
  } = useContext(ReverseCollectContext);

  const handleGetFile = () => {
    inputFileRef.current.click();
  };

  const handleAddInvoiceFile = async (event) => {
    const { files } = event.target;

    if (files?.length > 0) {
      const filteredFiles = [];

      Object.entries(files).forEach(([_, file]) => {
        if (file.type === 'text/xml') {
          filteredFiles.push(file);
        }
      });

      if (filteredFiles.length < files.length) {
        toast(
          <Toast
            title="Atenção!"
            content="Tipo de arquivo deve ser XML."
            type="error"
          />,
          options
        );
      }

      if (filteredFiles.length === 0) return;

      setIsReadingFile(true);

      const newInvoiceFiles = [];
      const fileDataList = [];
      let hasInvalidDocumentModal = false;

      // eslint-disable-next-line no-restricted-syntax
      for await (const file of filteredFiles) {
        const response = await readTaxDocument({
          file
        });

        if (response?.status === 200) {
          const { data } = response.data;

          let senderDocument = data.remetente?.cnpj || data.remetente?.cpf;

          let userDocument = user?.document;

          while (String(senderDocument)?.split('')[0] === '0') {
            senderDocument = String(senderDocument).substring(1);
          }

          while (String(userDocument)?.split('')[0] === '0') {
            userDocument = String(userDocument).substring(1);
          }

          if (String(senderDocument) === String(userDocument)) {
            fileDataList?.push(data);

            newInvoiceFiles?.push({
              id: uuid(),
              file,
              name: file.name,
              size: file.size,
              readableSize: filesize(file.size),
              invoiceNumber: data?.numero || '',
              // eslint-disable-next-line prettier/prettier, prefer-template
              invoiceDate: data?.dataDeSaida ? new Date(String(data?.dataDeSaida + 'T00:00:00.000')) : ''
            });
          } else {
            hasInvalidDocumentModal = true;
          }
        }
      }

      if (newInvoiceFiles?.length > 0) {
        setDocumentType('invoiceIssued');
        setFileAmount(String(newInvoiceFiles?.length));
        setInvoiceFiles(newInvoiceFiles);
      }

      if (fileDataList?.length > 0) {
        const [data] = fileDataList;

        const senderDocument = data.remetente?.cnpj || data.remetente?.cpf;
        const recipientDocument =
          data.destinatario?.cnpj || data.destinatario?.cpf;

        const newAddressList = [...addressList];

        const newAddress = {
          name: data.remetente?.nome?.toUpperCase(),
          document: senderDocument ? parseCpfCnpj(senderDocument) : '',
          zipPostalCode: parseCpfCnpj(data.remetente?.endereco?.cep),
          streetAddress: data.remetente?.endereco?.logradouro?.toUpperCase(),
          streetAddressNumber: data.remetente?.endereco?.numero,
          additionalInformation: data.remetente?.endereco?.complemento
            ? data.remetente?.endereco?.complemento?.toUpperCase()
            : '',
          suburb: data.remetente?.endereco?.bairro?.toUpperCase(),
          city: data.remetente?.endereco?.municipio?.toUpperCase(),
          state: data.remetente?.endereco?.uf?.toUpperCase()
        };

        if (userAddressList?.length > 0) {
          newAddress.contactEmail = userAddressList[0].contactEmail || '';
          newAddress.contactExtension =
            userAddressList[0].contactExtension || '';
          newAddress.contactName = userAddressList[0].contactName || '';
          // eslint-disable-next-line prettier/prettier
          newAddress.contactNumber = userAddressList[0].contactNumber ? String(parsePhone(userAddressList[0].contactNumber)).padStart(10, '0') : '';
        }

        newAddressList.push(newAddress);

        setAddressList(newAddressList);
        setSelectedAddressIndex(newAddressList.length - 1);

        setCollectSenderData({
          document: recipientDocument ? parseCpfCnpj(recipientDocument) : '',
          name: data.destinatario?.nome?.toUpperCase(),
          address: {
            zipPostalCode: parseCpfCnpj(data.destinatario?.endereco?.cep),
            streetAddress:
              data.destinatario?.endereco?.logradouro?.toUpperCase(),
            streetAddressNumber: data.destinatario?.endereco?.numero,
            suburb: data.destinatario?.endereco?.bairro?.toUpperCase(),
            city: data.destinatario?.endereco?.municipio?.toUpperCase(),
            state: data.destinatario?.endereco?.uf?.toUpperCase(),
            country: data.destinatario?.endereco?.pais
              ? data.destinatario?.endereco?.pais.toUpperCase()
              : 'BRASIL',
            additionalInformation: data.destinatario?.endereco?.complemento
              ? data.destinatario?.endereco?.complemento?.toUpperCase()
              : ''
          },
          contactList: [
            {
              name: '',
              phoneNumber: '',
              extension: '',
              email: ''
            }
          ]
        });

        setDisabledSenderData(true);
      }

      if (hasInvalidDocumentModal) {
        setModalSelected('hasInvalidDocumentModal');
        setIsReadingFile(false);
        return;
      }

      setHasCreatePickupWithInvoice(true);
      closeModal();
    }
  };

  const handleCloseModal = () => {
    closeModal();
  };

  return (
    <>
      {!isReadingFile ? (
        <>
          {modalSelected === 'wishImportInvoiceModal' && (
            <Modal closeFunction={handleCloseModal}>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16
                }}
              >
                <ModalTitle>Atenção</ModalTitle>

                <ModalText>
                  Deseja utilizar os dados do XML para preenchimento da
                  solicitação?
                </ModalText>

                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 32,
                    marginTop: 16
                  }}
                >
                  <Button
                    type="button"
                    variant="contained"
                    color="attention"
                    onClick={handleCloseModal}
                  >
                    Não
                  </Button>

                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleGetFile}
                  >
                    Sim
                  </Button>

                  <input
                    type="file"
                    ref={inputFileRef}
                    id="invoice-file"
                    data-testid="addInvoiceFileInput"
                    accept=".xml"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleAddInvoiceFile}
                  />
                </Box>
              </Box>
            </Modal>
          )}

          {modalSelected === 'hasInvalidDocumentModal' && (
            <Modal width={480} preventClose>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16
                }}
              >
                <ModalTitle>Atenção</ModalTitle>

                <ModalText>
                  O remetente contido no documento fiscal não está vinculado ao
                  solicitante ({user?.name}).
                  <br />
                  <br />
                  Em caso de dúvidas entre em contato com o departamento de
                  coleta da sua filial.
                </ModalText>

                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 32,
                    marginTop: 16
                  }}
                >
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={handleCloseModal}
                  >
                    Ok
                  </Button>
                </Box>
              </Box>
            </Modal>
          )}
        </>
      ) : (
        <Modal transparent preventClose>
          <ReactLoading type="spin" color="#fff" />
        </Modal>
      )}
    </>
  );
};
